// Auth wiring (PLAN-C1.3 F1d) — first-party httpOnly cookie session on the shared aquinocompliance.com
// domain. The SPA loads its session from the auth-worker (/auth/me, credentialed so the cookie travels)
// and redirects unauthenticated users to AuthKit (/auth/login). The token is never readable by JS
// (httpOnly) — no localStorage, no XSS surface. The 15-min access token is kept alive PROACTIVELY
// (startSessionKeepAlive) so requests never cross an expired boundary; a stray 401 is still recovered
// by the api-client's single-flight refresh-and-retry, and a 401 that survives that means the session
// is genuinely over → handleSessionExpired re-auths. B3-IDENTITY-BROKER / B3-SESSION-TOPOLOGY.
import type { SympllexSession } from "@sympllex/auth";
import { AUTH_BASE } from "./env";

async function fetchMe(): Promise<SympllexSession | null> {
  try {
    const r = await fetch(`${AUTH_BASE}/auth/me`, { credentials: "include" });
    if (!r.ok) return null;
    const data = (await r.json()) as { session?: SympllexSession };
    return data.session ?? null;
  } catch {
    return null;
  }
}

// De-duped silent refresh: rotates the 15-min access-token cookie off the 14-day session (sympllex_sid).
// Concurrent 401s share one in-flight POST /auth/refresh so we never stampede the endpoint.
let refreshInFlight: Promise<boolean> | null = null;
export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${AUTH_BASE}/auth/refresh`, { method: "POST", credentials: "include" })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/** Fetch the current session; if the access-token cookie expired, refresh once and retry before giving up. */
export async function loadSession(): Promise<SympllexSession | null> {
  const s = await fetchMe();
  if (s) return s;
  if (await refreshSession()) return fetchMe();
  return null;
}

let redirecting = false;
/** Send the browser to AuthKit hosted login (top-level navigation → SameSite cookie returns). Guarded
 *  so concurrent callers (e.g. several failed queries at once) trigger exactly one navigation. */
export function redirectToLogin(): void {
  if (redirecting || typeof window === "undefined") return;
  redirecting = true;
  window.location.href = `${AUTH_BASE}/auth/login`;
}

/** A request returned 401 even after the silent refresh-and-retry → the session is genuinely over
 *  (idle/absolute timeout or revoked). Re-establish it through AuthKit (seamless while WorkOS SSO is
 *  still valid). Loop-guarded via redirectToLogin. */
export function handleSessionExpired(): void {
  redirectToLogin();
}

// Proactive keep-alive: rotate the access token a few minutes BEFORE its 15-min expiry so requests
// never hit an expired boundary (the reactive refresh-and-retry is the safety net, not the norm). Also
// refresh on tab-focus/online — background tabs throttle timers and laptops sleep, so a returning user
// gets a fresh token instead of a 401 on their next action. Idempotent; browser-only.
const KEEPALIVE_INTERVAL_MS = 12 * 60 * 1000; // 15-min token, ~3-min safety margin
let keepAliveStarted = false;
export function startSessionKeepAlive(): void {
  if (keepAliveStarted || typeof window === "undefined") return;
  keepAliveStarted = true;
  window.setInterval(() => { void refreshSession(); }, KEEPALIVE_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void refreshSession();
  });
  window.addEventListener("online", () => { void refreshSession(); });
}

/** Revoke the session server-side, then bounce to login. */
export async function logout(): Promise<void> {
  await fetch(`${AUTH_BASE}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
  redirectToLogin();
}
